/**
 * Login status indicator.
 *
 * `LoginStatusPill` renders a single login's status, either as the full
 * `status-badge status-badge--<status>` badge (used inline in LoginsPanel
 * rows) or, in `compact` mode, as a small dot + name suitable for a header
 * strip.
 *
 * `LoginStatusPills` is the compact row wrapper used in WorkspacePage's
 * header — it owns the `useLogins()` query itself so the workspace header
 * doesn't need to know anything about the logins feature beyond this one
 * component.
 */

import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useLogins } from '../hooks/useLogins';
import type { LoginStatus } from '../types';
import { ROUTES } from '@utils/constants';

const STATUS_LABELS: Record<LoginStatus['status'], string> = {
  connected: 'Connected',
  attention: 'Attention',
  not_connected: 'Not connected',
};

const REASON_LABELS: Record<string, string> = {
  needs_link: 'Needs linking',
  needs_reauth: 'Needs re-authentication',
  provider_error: 'Provider error',
};

function tooltipFor(login: LoginStatus): string {
  const detail = login.identity ?? (login.reason ? REASON_LABELS[login.reason] : STATUS_LABELS[login.status]);
  return `${login.display_name}: ${detail ?? STATUS_LABELS[login.status]}`;
}

interface LoginStatusPillProps {
  login: LoginStatus;
  compact?: boolean;
}

export function LoginStatusPill({ login, compact = false }: LoginStatusPillProps): ReactElement {
  if (compact) {
    return (
      <Link
        to={ROUTES.HOME}
        className="login-status-pill"
        title={tooltipFor(login)}
      >
        <span
          className={`login-status-pill__dot login-status-pill__dot--${login.status}`}
          aria-hidden="true"
        />
        <span className="login-status-pill__name">{login.display_name}</span>
      </Link>
    );
  }

  return (
    <span className={`status-badge status-badge--${login.status}`} title={tooltipFor(login)}>
      {STATUS_LABELS[login.status]}
    </span>
  );
}

interface LoginStatusPillsProps {
  compact?: boolean;
}

export function LoginStatusPills({ compact = true }: LoginStatusPillsProps): ReactElement | null {
  const { data: logins } = useLogins();

  if (!logins || logins.length === 0) return null;

  return (
    <div className="login-status-pills" role="list" aria-label="Login status">
      {logins.map((login) => (
        <LoginStatusPill key={login.name} login={login} compact={compact} />
      ))}
    </div>
  );
}
